/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";

// import tutorsParollTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollTableData"

// import DraftTutorePayrollTableData from "Views/FinanceManagment/DraftTutorSalary/data/draftTutorSalaryTableData";

// import supportTicketTableData from "Views/Activities/SupportTicket/data/supportTicketTableData";

import { useState, useEffect } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import tabs from "assets/theme/components/tabs";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Dropdown from "components/CustomDropdown/Dropdown";
import Invoices from "layouts/billing/components/Invoices";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import FileUploader from "components/FileUploader/FileUploader";
import api, { baseURL } from "utils/api";
import ArgonBadge from "components/ArgonBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PaginationBar from "components/PaginationBar/PaginationBar";
import { formatCycleLabel } from "utils/formatCycleLabel";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import CreateInvoice from "./CreateInvoice";
import ManualInvoices from "../ManualInvoices";

const profileLinkSx = (enabled) => ({
  cursor: enabled ? "pointer" : "default",
  textDecoration: enabled ? "underline !important" : "none",
  textUnderlineOffset: enabled ? "2px" : undefined,
  textDecorationThickness: enabled ? "1px" : undefined,
  "&:hover": enabled
    ? {
        color: "#1976d2",
        textDecoration: "underline !important",
      }
    : {},
});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatWholeRupees = (value) =>
  `\u20B9${Math.round(toNumber(value)).toLocaleString("en-IN")}`;

const shouldShowPaymentDueMarker = (invoice) =>
  invoice.status === "PENDING" && toNumber(invoice.creditCompletionPercentage) >= 100;

const getDatePickerValue = (value) => {
  if (!value || !dayjs(value).isValid()) return null;
  return dayjs(value);
};

const getEditablePaidRenewalInvoices = (invoice) => {
  const paidInvoices = Array.isArray(invoice?.invoices)
    ? invoice.invoices.filter((item) => item?.status === "PAID")
    : [];

  return paidInvoices
    .sort((a, b) => {
      const aCycle = Number(a?.sourceCycleNumber || a?.sourceCycleIndex || 0);
      const bCycle = Number(b?.sourceCycleNumber || b?.sourceCycleIndex || 0);
      if (aCycle !== bCycle) return aCycle - bCycle;

      const aTime = new Date(a?.createdAt || 0).getTime();
      const bTime = new Date(b?.createdAt || 0).getTime();
      return aTime - bTime;
    });
};

const renewalDatePickerPopperSx = (theme) => ({
  zIndex: theme.zIndex.modal + 100,
});

const renewalDatePickerPopperModifiers = [
  {
    name: "flip",
    enabled: true,
    options: {
      fallbackPlacements: ["top-start", "bottom-start", "top-end"],
    },
  },
  {
    name: "preventOverflow",
    enabled: true,
    options: {
      boundary: "viewport",
      rootBoundary: "viewport",
      padding: 16,
    },
  },
];

function Tables() {
  const [aadhaarFiles, setAadhaarFiles] = useState([]);

  const [open, setOpen] = useState(false);
  const handleAddClick = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const [dob, setDob] = useState(dayjs());

  // Tab change handler
  const handleTabChange = (status) => {
    setActiveTab(status);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Refetch data immediately with new status
    fetchInvoices(1, pagination.limit, search, status);
  };

  // Calculate dynamic tab data
  const getTabData = () => {
    return [
      { label: "All", count: statusCounts.ALL, color: "#2027e1ff", content: "", status: "All" },
      {
        label: "Pending",
        count: statusCounts.PENDING,
        color: "#ec10c7ff",
        content: "",
        status: "PENDING",
      },
      { label: "Paid", count: statusCounts.PAID, color: "#10a6ecff", content: "", status: "PAID" },
      {
        label: "Failed",
        count: statusCounts.FAILED,
        color: "#ec3c10ff",
        content: "",
        status: "FAILED",
      },
      { label: "Void", count: statusCounts.VOID, color: "#1a6808b7", content: "", status: "VOID" },
    ];
  };

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [search, setSearch] = useState("");

  // New state for API data
  const [invoices, setInvoices] = useState([]);
  const [invoiceTotals, setInvoiceTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
  });
  const [statusCounts, setStatusCounts] = useState({
    ALL: 0,
    PENDING: 0,
    PAID: 0,
    FAILED: 0,
    VOID: 0,
  });
  const [activeTab, setActiveTab] = useState("All");
  const [showManualInvoices, setShowManualInvoices] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [dateEditorOpen, setDateEditorOpen] = useState(false);
  const [dateEditorSaving, setDateEditorSaving] = useState(false);
  const [selectedRenewalInvoice, setSelectedRenewalInvoice] = useState(null);
  const [dateEditorForm, setDateEditorForm] = useState({
    renewals: [],
  });

  // Fetch invoices from API
  const fetchInvoices = async (
    page = 1,
    limit = pagination.limit,
    searchQuery = search,
    tabStatus = activeTab
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };

      if (fromDate) params.from = fromDate.format("YYYY-MM-DD");
      if (toDate) params.to = toDate.format("YYYY-MM-DD");
      if (searchQuery) params.search = searchQuery;
      if (tabStatus !== "All") params.status = tabStatus;

      const response = await api.get("/finance/get-all-raise-invoice", { params });

      if (response.data.success) {
        // Prefer backend replacement-aware GM. Fallback supports older API responses.
        const invoicesWithGrossMargin = response.data.data.map((invoice) => {
          const tutorSalary = toNumber(invoice.classSessionId?.[0]?.tutorSalary);
          const totalClasses = toNumber(invoice.totalClasses);
          const totalAmount = toNumber(invoice.totalAmount);
          const fallbackTutorCost = tutorSalary * totalClasses;
          const tutorCost = toNumber(invoice.totalTutorCost || invoice.totalTutorPay) || fallbackTutorCost;
          const calculatedGrossMargin =
            invoice.grossMarginAmount !== undefined
              ? toNumber(invoice.grossMarginAmount)
              : totalAmount - tutorCost;
          const grossMarginPercentage =
            invoice.grossMarginPercentage !== undefined
              ? toNumber(invoice.grossMarginPercentage).toFixed(2)
              : totalAmount > 0
              ? ((calculatedGrossMargin / totalAmount) * 100).toFixed(2)
              : 0;

          return {
            ...invoice,
            grossMargin: calculatedGrossMargin,
            grossMarginPercentage,
            totalTutorCost: tutorCost,
          };
        });

        setInvoices(invoicesWithGrossMargin);
        setInvoiceTotals(response.data.totals || null);
        setPagination(response.data.pagination);
        setStatusCounts(response.data.statusCounts);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch data on mount and filter changes
  useEffect(() => {
    fetchInvoices();
  }, [fromDate, toDate]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    fetchInvoices(page);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = event.target.value;
    setPagination((prev) => ({ ...prev, limit: newLimit, currentPage: 1 }));
    fetchInvoices(1, newLimit);
  };

  // Search handler
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchInvoices(1);
  };

  // Search on enter key
  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // Reset date filters
  const handleResetDates = () => {
    setFromDate(null);
    setToDate(null);
  };

  // Open the API URL directly so the PDF viewer keeps the server-provided filename.
  const handleDownloadInvoice = (invoiceId) => {
    if (downloadingInvoiceId) return;

    const invoiceUrl = `${baseURL}/finance/invoices/${encodeURIComponent(invoiceId)}/download`;
    setDownloadingInvoiceId(invoiceId);

    window.setTimeout(() => {
      const previewWindow = window.open(invoiceUrl, "_blank");

      if (!previewWindow) {
        setDownloadingInvoiceId(null);
        alert("Failed to open invoice. Please allow popups and try again.");
        return;
      }

      setDownloadingInvoiceId(null);
    }, 2000);
  };

  const buildDateEditorRenewals = (editableRenewals) =>
    editableRenewals.map((renewal, index) => ({
      id: renewal._id,
      rowKey: renewal.rowKey || `${renewal._id}-${renewal.transactionId || index}`,
      transactionId: renewal.transactionId,
      label: renewal.sourceCycleNumber
        ? `Cycle ${renewal.sourceCycleNumber}`
        : renewal.invoiceType === "ACTIVATION" && index === 0
        ? "Activation"
        : `Payment ${index + 1}`,
      amount: renewal.totalAmount,
      paymentDate: getDatePickerValue(renewal.paymentDate || renewal.paidAt),
      startDate: getDatePickerValue(renewal.startDate),
    }));

  const handleOpenDateEditor = async (invoice) => {
    let editableRenewals = getEditablePaidRenewalInvoices(invoice);

    try {
      const response = await api.get(`/finance/invoices/${invoice._id}/related-dates`);
      if (response.data.success) {
        editableRenewals = getEditablePaidRenewalInvoices({
          invoices: response.data.data?.invoices || [],
        });
      }
    } catch (error) {
      console.error("Error fetching related invoice dates:", error);
    }

    if (!editableRenewals.length) {
      toast.error("No paid renewal invoice found for this class.");
      return;
    }

    setSelectedRenewalInvoice(invoice);
    setDateEditorForm({
      renewals: buildDateEditorRenewals(editableRenewals),
    });
    setDateEditorOpen(true);
  };

  const handleCloseDateEditor = () => {
    if (dateEditorSaving) return;
    setDateEditorOpen(false);
    setSelectedRenewalInvoice(null);
    setDateEditorForm({ renewals: [] });
  };

  const handleSaveRenewalDates = async () => {
    if (!selectedRenewalInvoice?._id && !dateEditorForm.renewals.length) return;

    const updates = dateEditorForm.renewals.map((renewal) => ({
      id: renewal.id,
      transactionId: renewal.transactionId,
      paymentDate: renewal.paymentDate ? renewal.paymentDate.format("YYYY-MM-DD") : undefined,
      startDate: renewal.startDate ? renewal.startDate.format("YYYY-MM-DD") : undefined,
    }));

    if (!updates.some((item) => item.paymentDate || item.startDate)) {
      toast.error("Please enter at least one payment date.");
      return;
    }

    setDateEditorSaving(true);
    try {
      await api.put(`/finance/invoices/${updates[0].id}/dates`, {
        updates,
      });
      toast.success("Invoice dates updated successfully");
      await fetchInvoices(pagination.currentPage, pagination.limit);
      setDateEditorOpen(false);
      setSelectedRenewalInvoice(null);
      setDateEditorForm({ renewals: [] });
    } catch (error) {
      console.error("Error updating renewal dates:", error);
      toast.error(error?.response?.data?.message || "Failed to update renewal dates.");
    } finally {
      setDateEditorSaving(false);
    }
  };

  // Build table rows from API data
  const buildTableRows = () => {
    return invoices.map((invoice, index) => {
      const parentId = invoice.parentId?._id || invoice.parentId?.id;
      const parentProfilePath = parentId ? `/parents/profile/${parentId}` : undefined;

      return {
        sno: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {(pagination.currentPage - 1) * pagination.limit + index + 1}
          </ArgonTypography>
        ),
        "client name": (
          <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
            <ArgonBox display="flex" flexDirection="column">
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                component={parentProfilePath ? Link : "span"}
                to={parentProfilePath}
                color="text"
                sx={profileLinkSx(Boolean(parentProfilePath))}
              >
                {invoice.parentId?.fullName || "N/A"}
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary">
                {invoice.parentId?.email || ""}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        ),
      cycle: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {formatCycleLabel(invoice.totalCycles || invoice.classSessionId?.[0]?.noOfMonths)}
        </ArgonTypography>
      ),
      // Amount: (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     ₹{invoice.totalTutorPay || 0}
      //   </ArgonTypography>
      // ),
      "date & time sent": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {dayjs(invoice.invoiceDate || invoice.createdAt).format("DD MMM YYYY, hh:mm A")}
        </ArgonTypography>
      ),
      "Total PAID": (
        <ArgonTypography variant="caption" color="success" fontWeight="medium">
          {formatWholeRupees(invoice.totalAmount)}
        </ArgonTypography>
      ),
      "Due Amount": (
        <ArgonTypography
          variant="caption"
          color={invoice.status === "PENDING" ? "warning" : "text"}
          fontWeight="medium"
        >
          {formatWholeRupees(invoice.status === "PENDING" ? invoice.pendingAmount : 0)}
        </ArgonTypography>
      ),
      "Gross Margin": (
        <ArgonTypography variant="caption" fontWeight="medium">
          <span style={{ color: "#17c1e8" }}>{formatWholeRupees(invoice.grossMargin)}</span>{" "}
          <span style={{ color: "#67748E" }}>({invoice.grossMarginPercentage || 0}%)</span>
        </ArgonTypography>
      ),
      class: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {invoice.students?.[0]?.class?.name || "N/A"}
        </ArgonTypography>
      ),
      status: (
        <ArgonBox position="relative" display="inline-flex">
          <ArgonBadge
            variant="gradient"
            badgeContent={invoice.status}
            color={
              invoice.status === "PAID"
                ? "success"
                : invoice.status === "PENDING"
                ? "warning"
                : "error"
            }
            size="xs"
            container
          />
          {shouldShowPaymentDueMarker(invoice) && (
            <ArgonTypography
              component="span"
              title="Cycle completed - payment due"
              aria-label="Cycle completed - payment due"
              sx={{
                position: "absolute",
                top: "-12px",
                right: "-9px",
                color: "#fb8c00",
                fontSize: "15px",
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {"\u20B9"}
            </ArgonTypography>
          )}
        </ArgonBox>
      ),
      download: (
        <ArgonBox display="flex" alignItems="center" gap={1}>
          {getEditablePaidRenewalInvoices(invoice).length > 0 && (
            <Tooltip title="Edit invoice dates">
              <IconButton size="small" color="primary" onClick={() => handleOpenDateEditor(invoice)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <ArgonTypography
            component="a"
            href="#"
            variant="body1"
            color="primary"
            fontWeight="medium"
            onClick={(e) => {
              e.preventDefault();
              handleDownloadInvoice(invoice._id);
            }}
            sx={{ cursor: downloadingInvoiceId ? "default" : "pointer" }}
          >
            {downloadingInvoiceId === invoice._id ? (
              <CircularProgress size={18} thickness={5} color="inherit" />
            ) : (
              <DownloadIcon />
            )}
          </ArgonTypography>
          {downloadingInvoiceId === invoice._id && (
            <ArgonTypography variant="caption" color="secondary">
              Opening...
            </ArgonTypography>
          )}
          {/* <ArgonTypography component="a" href="#" variant="body1" color="info" fontWeight="medium">
            <VisibilityIcon />
          </ArgonTypography>
          <ArgonTypography component="a" href="#" variant="body1" color="error" fontWeight="medium">
            <DeleteIcon />
          </ArgonTypography> */}
        </ArgonBox>
      ),
      };
    });
  };

  const totalGrossMarginPercentage = invoiceTotals
    ? (toNumber(invoiceTotals.totalPaid) > 0
        ? (toNumber(invoiceTotals.grossMargin) / toNumber(invoiceTotals.totalPaid)) * 100
        : 0).toFixed(2)
    : null;

  const amountHeader = (name, amount, color, percentage = null) => ({
    name,
    label: (
      <ArgonBox component="span" display="inline-flex" flexDirection="column" gap={0.5}>
        <ArgonTypography
          component="span"
          variant="caption"
          fontWeight="bold"
          sx={{ color: "#7686a6ff", opacity: 0.7, fontSize: "inherit" }}
        >
          {name.toUpperCase()}
        </ArgonTypography>
        <ArgonTypography
          component="span"
          variant="caption"
          color={color}
          fontWeight="bold"
          sx={{ fontSize: "inherit" }}
        >
          ({amount == null ? "—" : formatWholeRupees(amount)})
          {percentage !== null && ` ${percentage}%`}
        </ArgonTypography>
      </ArgonBox>
    ),
    align: "left",
    headerOpacity: 1,
  });

  const tableData = {
    columns: [
      { name: "sno", align: "center" },
      { name: "client name", align: "left" },
      { name: "cycle", align: "left" },
      // { name: "Amount", align: "left" },
      { name: "date & time sent", align: "left" },
      amountHeader("Total PAID", invoiceTotals?.totalPaid, "success"),
      amountHeader("Due Amount", invoiceTotals?.dueAmount, "warning"),
      amountHeader("Gross Margin", invoiceTotals?.grossMargin, "info", totalGrossMarginPercentage),
      { name: "class", align: "center" },
      { name: "status", align: "center" },
      { name: "download", align: "center" },
    ],
    rows: buildTableRows(),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {showManualInvoices ? (
        <ArgonBox py={3}><ManualInvoices onBack={() => setShowManualInvoices(false)} /></ArgonBox>
      ) : <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Raise invoice
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center" gap={1.5}>
                  <ArgonButton variant="outlined" color="primary" onClick={() => setShowManualInvoices(true)}>
                    View Manual Invoice
                  </ArgonButton>
                  <CreateInvoice />
                </ArgonBox>
              </ArgonBox>

              <ArgonBox sx={{ width: "fit-content" }}>
                <ApnaTabs tabsData={getTabData()} onTabChange={handleTabChange} />
              </ArgonBox>

              <ArgonBox p={2}>
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2} // equal horizontal + vertical gap
                >
                  <ArgonBox display="flex" alignItems="center" gap={2}>
                    {/* Label */}
                    <ArgonTypography variant="subtitle2" fontWeight="bold" color="text">
                      Filters
                    </ArgonTypography>

                    <CustomDatePicker
                      label="From"
                      value={fromDate}
                      onChange={(newValue) => setFromDate(newValue)}
                      width={160}
                      maxDate={dayjs()} // future date disable
                    />
                    <CustomDatePicker
                      label="To"
                      value={toDate}
                      onChange={(newValue) => setToDate(newValue)}
                      width={160}
                      maxDate={dayjs()} // future date disable
                      minDate={fromDate}
                    />

                    {/* Reset Button */}
                    <ArgonButton
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={handleResetDates}
                      disabled={!fromDate && !toDate}
                    >
                      Reset
                    </ArgonButton>
                  </ArgonBox>

                  <ArgonBox display="flex" alignItems="center" gap={2}>
                    {/* Search Field */}
                    <ArgonBox sx={{ minWidth: "250px" }}>
                      <ArgonInput
                        fullWidth
                        size="small"
                        placeholder="Search by Name, Email, Cycle"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                      />
                    </ArgonBox>

                    {/* Search Button */}
                    <ArgonButton
                      variant="contained"
                      size="small"
                      color="primary"
                      sx={{ minWidth: "100px" }}
                      onClick={handleSearch}
                    >
                      Search
                    </ArgonButton>

                    {/* Add Button
                    <ArgonButton
                      variant="contained"
                      size="small"
                      color="primary"
                      sx={{ minWidth: "100px" }}
                      onClick={handleAddClick}
                    >
                      Add
                    </ArgonButton> */}
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching invoices...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : invoices.length > 0 ? (
                <Table columns={tableData.columns} rows={tableData.rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {activeTab === "All"
                      ? "No invoices found"
                      : `No ${activeTab.toLowerCase()} invoices found`}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {activeTab === "All"
                      ? "Try adjusting your filters or check back later."
                      : `No invoices with ${activeTab.toLowerCase()} status found.`}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {invoices.length > 0 && (
              <PaginationBar
                currentPage={pagination.currentPage}
                rowsPerPage={pagination.limit}
                totalItems={pagination.totalRecords}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>

          {/* Modal */}
          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
              style: {
                borderRadius: 20,
                padding: "20px",
                background: "#f9fafc", // light background for better contrast
              },
            }}
          >
            {/* Header */}
            <DialogTitle sx={{ pb: 1 }}>
              <ArgonTypography variant="h4" fontWeight="bold" color="info">
                🧾 Raise Invoice
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text" mt={0.5}>
                Fill in the details below to generate a new invoice
              </ArgonTypography>
            </DialogTitle>

            {/* Body */}
            <DialogContent dividers>
              <form>
                <Grid container spacing={3}>
                  {/* Invoice ID */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Invoice ID
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Invoice ID" name="invoice_number" />
                  </Grid>

                  {/* Client Dropdown */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Client
                    </ArgonTypography>
                    <Dropdown options={["Client 1", "Client 2"]} width="100%" />
                  </Grid>

                  {/* Class Dropdown */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Class
                    </ArgonTypography>
                    <Dropdown options={["Class 1", "Class 2"]} width="100%" />
                  </Grid>

                  {/* Cycle */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Cycle
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Cycle" />
                  </Grid>

                  {/* Total Amount */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Total Amount
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Amount" name="total_amount" />
                  </Grid>

                  {/* Due Amount */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Due Amount
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      placeholder="Enter Amount"
                      name="due_amount"
                      defaultValue="0"
                    />
                  </Grid>

                  {/* Invoice Amount */}
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Invoice Amount
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Amount" name="invoice_amount" />
                  </Grid>

                  {/* Upload Invoice */}
                  <Grid item xs={12} md={6}>
                    <FileUploader
                      label="Upload Invoice"
                      files={aadhaarFiles}
                      setFiles={setAadhaarFiles}
                      accept="image/*,application/pdf"
                      multiple={false}
                      Width="100%"
                      Height={120}
                    />
                  </Grid>

                  {/* Notes */}
                  <Grid item xs={12}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Note
                    </ArgonTypography>
                    <ArgonInput multiline rows={3} placeholder="Enter Note" fullWidth />
                  </Grid>
                </Grid>

                {/* Footer Buttons */}
                <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleClose}
                    sx={{ px: 4, py: 1.2, borderRadius: "10px" }}
                  >
                    Cancel
                  </ArgonButton>

                  <ArgonButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    sx={{ px: 4, py: 1.2, borderRadius: "10px", fontWeight: "bold" }}
                  >
                    Save Invoice
                  </ArgonButton>
                </ArgonBox>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog
            open={dateEditorOpen}
            onClose={handleCloseDateEditor}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { mt: -10, overflow: "visible", maxHeight: "none" } }}
          >
            <DialogTitle sx={{ px: 2.5, py: 1.5 }}>
              <ArgonTypography variant="h6" fontWeight="medium">
                Edit renewal payment dates
              </ArgonTypography>
            </DialogTitle>
            <DialogContent dividers sx={{ overflow: "visible", px: 2, py: 1.5 }}>
              <ArgonBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", md: "1fr 0.65fr 0.85fr 150px" }}
                gap={1}
                alignItems="center"
                px={0.5}
                pb={0.75}
                sx={{ display: { xs: "none", md: "grid" } }}
              >
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  Invoice
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  Amount
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  Current date
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  New date
                </ArgonTypography>
              </ArgonBox>
              <ArgonBox display="flex" flexDirection="column" gap={0.75} pt={0.25}>
                {dateEditorForm.renewals.map((renewal, index) => (
                  <ArgonBox
                    key={renewal.rowKey || renewal.id}
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "1fr 0.65fr 0.85fr 150px" }}
                    gap={1}
                    alignItems="center"
                    px={1.25}
                    py={0.9}
                    sx={{
                      overflow: "visible",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      backgroundColor: "#fbfcff",
                    }}
                  >
                    <ArgonBox display="flex" flexDirection="column">
                      <ArgonTypography variant="button" fontWeight="medium">
                        {renewal.label}
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {formatWholeRupees(renewal.amount)}
                    </ArgonTypography>
                    <ArgonBox display="flex" flexDirection="column">
                      <ArgonTypography
                        variant="caption"
                        color="secondary"
                        sx={{ display: { xs: "inline", md: "none" } }}
                      >
                        Current date
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        {renewal.paymentDate ? renewal.paymentDate.format("DD MMM YYYY") : "-"}
                      </ArgonTypography>
                    </ArgonBox>
                    <CustomDatePicker
                      label=""
                      value={renewal.paymentDate}
                      onChange={(newValue) =>
                        setDateEditorForm((prev) => ({
                          ...prev,
                          renewals: prev.renewals.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, paymentDate: newValue }
                              : item,
                          ),
                        }))
                      }
                      width={150}
                      maxDate={dayjs()}
                      popperSx={renewalDatePickerPopperSx}
                      popperPlacement="bottom-start"
                      popperModifiers={renewalDatePickerPopperModifiers}
                    />
                  </ArgonBox>
                ))}
              </ArgonBox>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
              <ArgonButton
                variant="outlined"
                color="secondary"
                onClick={handleCloseDateEditor}
                disabled={dateEditorSaving}
              >
                Cancel
              </ArgonButton>
              <ArgonButton
                variant="contained"
                color="primary"
                onClick={handleSaveRenewalDates}
                disabled={dateEditorSaving}
              >
                {dateEditorSaving ? "Saving..." : "Save"}
              </ArgonButton>
            </DialogActions>
          </Dialog>
        </ArgonBox>
      </ArgonBox>}
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
